import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Plus, DownloadSimple, FilePdf, Trash, PencilSimple, WhatsappLogo,
  Money, MagnifyingGlass, CaretUp, CaretDown, Receipt, Warning, FileArrowUp, LockSimple, EnvelopeSimple,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const statusColor = {
  New: "bg-[#555] text-white", "In Progress": "bg-[#FFAB00] text-black",
  Completed: "bg-[#0047FF] text-white", Delivered: "bg-[#00C853] text-white",
  "Full Payment Received": "bg-[#0a7d2c] text-white", Cancelled: "bg-[#e11d48] text-white",
};

const emptyForm = {
  student_name: "", student_phone: "", student_email: "", country: "", subject: "", word_count: 0, booking_amount: 0, net_payment: 0,
  expected_completion_date: "", agent_id: "", writer_id: "", status: "New", notes: "",
};

const emptyPay = { party: "student", amount: "", date: "", note: "", file: null };

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isWriter = user?.role === "writer";
  const isAgent = user?.role === "agent";
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ countries: [], statuses: [] });
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [exporting, setExporting] = useState(false);

  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [agentFilter, setAgentFilter] = useState("all");
  const [writerFilter, setWriterFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [payLead, setPayLead] = useState(null);
  const [payForm, setPayForm] = useState(emptyPay);

  const load = useCallback(() => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [load]);

  const agents = users.filter((u) => u.role === "agent");
  const writers = users.filter((u) => u.role === "writer");

  const filtered = useMemo(() => {
    let rows = [...leads];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((l) =>
      [l.assignment_code, l.student_name, l.subject, l.agent_name, l.writer_name]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q)));
    if (countryFilter !== "all") rows = rows.filter((l) => l.country === countryFilter);
    if (statusFilter !== "all") rows = rows.filter((l) => l.status === statusFilter);
    if (overdueOnly) rows = rows.filter((l) => l.is_overdue);
    if (agentFilter !== "all") rows = rows.filter((l) => l.agent_id === agentFilter);
    if (writerFilter !== "all") rows = rows.filter((l) => l.writer_id === writerFilter);
    if (monthFilter !== "all") rows = rows.filter((l) => (l.created_at || "").slice(0, 7) === monthFilter);
    const { key, dir } = sort;
    rows.sort((a, b) => {
      let av = a[key], bv = b[key];
      if (["word_count", "booking_amount", "amount_received"].includes(key)) { av = Number(av || 0); bv = Number(bv || 0); }
      else { av = (av || "").toString().toLowerCase(); bv = (bv || "").toString().toLowerCase(); }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [leads, search, countryFilter, statusFilter, sort, overdueOnly, agentFilter, writerFilter, monthFilter]);

  const monthOptions = useMemo(() => {
    const s = new Set();
    leads.forEach((l) => { if (l.created_at) s.add(l.created_at.slice(0, 7)); });
    return Array.from(s).sort().reverse();
  }, [leads]);
  const monthLabel = (m) => new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" });

  const totals = useMemo(() => filtered.reduce((a, l) => {
    a.words += Number(l.word_count) || 0;
    a.booking += Number(l.booking_amount) || 0;
    a.received += Number(l.amount_received) || 0;
    a.pending += (Number(l.booking_amount) || 0) - (Number(l.amount_received) || 0);
    a.net += Number(l.net_payment) || 0;
    a.writer += Number(l.writer_payment_amount) || 0;
    return a;
  }, { words: 0, booking: 0, received: 0, pending: 0, net: 0, writer: 0 }), [filtered]);
  const totalsLabel = monthFilter === "all" ? "All months" : monthLabel(monthFilter);
  const agentName = agents.find((a) => a.id === agentFilter)?.name;
  const avgPerWord = totals.words > 0 ? totals.net / totals.words : 0;
  const fmtPerWord = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ ...emptyForm, ...l, student_phone: l.student_phone || "", student_email: l.student_email || "", expected_completion_date: l.expected_completion_date || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      word_count: Number(form.word_count) || 0,
      booking_amount: Number(form.booking_amount) || 0,
      agent_id: form.agent_id || null,
      writer_id: form.writer_id || null,
      expected_completion_date: form.expected_completion_date || null,
      student_phone: form.student_phone || null,
      student_email: form.student_email || null,
      net_payment: Number(form.net_payment) || 0,
    };
    if (isAdmin && editing && form.created_at) {
      payload.created_at = new Date(form.created_at).toISOString();
    }
    try {
      if (editing) { await api.put(`/leads/${editing.id}`, payload); toast.success("Lead updated"); }
      else { const { data } = await api.post("/leads", payload); toast.success(`Lead created · ${data.assignment_code}`); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const viewReceipt = async (ref) => {
    try {
      const res = await api.get(`/files/${ref.path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open receipt"); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "MHA_Leads.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "MHA_Import_Template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a file first"); return; }
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} lead(s) imported`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const openPayments = (l) => { setPayLead(l); setPayForm(emptyPay); };

  const sendReminder = async (l) => {
    if (!l.student_email) { toast.error("No student email on this lead"); return; }
    try {
      const { data } = await api.post(`/leads/${l.id}/remind`);
      toast.success(`Reminder emailed to ${data.to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addPayment = async () => {
    if (!payForm.amount) { toast.error("Enter an amount"); return; }
    const fd = new FormData();
    fd.append("party", payForm.party);
    fd.append("amount", payForm.amount);
    fd.append("date", payForm.date || "");
    fd.append("note", payForm.note || "");
    if (payForm.file) fd.append("file", payForm.file);
    try {
      const { data } = await api.post(`/leads/${payLead.id}/payments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment recorded");
      setPayLead(data); setPayForm(emptyPay); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePayment = async (pid) => {
    try {
      const { data } = await api.delete(`/leads/${payLead.id}/payments/${pid}`);
      toast.success("Payment removed");
      setPayLead(data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const waShare = (l) => {
    const pending = (l.booking_amount || 0) - (l.amount_received || 0);    const msg =
      `Hello ${l.student_name},\n\n` +
      `Here are your assignment details from MHA Writers:\n\n` +
      `Assignment Code: ${l.assignment_code}\n` +
      `Subject/Module: ${l.subject}\n` +
      `Word Count: ${Number(l.word_count).toLocaleString()}\n` +
      `Expected Delivery: ${l.expected_completion_date || "To be confirmed"}\n\n` +
      `Booking Amount: ${inr(l.booking_amount)}\n` +
      `Received: ${inr(l.amount_received)}\n` +
      `Pending: ${inr(pending)}\n\n` +
      `Thank you for choosing us.`;
    const phone = (l.student_phone || "").replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const SortHead = ({ label, k, align }) => (
    <th className={`px-4 py-3 cursor-pointer select-none ${align === "right" ? "text-right" : ""}`} onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs tracking-widest text-[#0047FF]">PIPELINE</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter">Leads</h2>
          <p className="text-sm text-[#555] mt-1">{filtered.length} of {leads.length} shown.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportExcel} disabled={exporting} variant="outline" className="rounded-none border-[#111] hover:bg-[#111] hover:text-white transition-colors" data-testid="export-excel-button">
            <DownloadSimple weight="bold" size={16} className="mr-2" /> {exporting ? "Exporting…" : "Export Excel"}
          </Button>
          {isAdmin && (
            <Button onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} variant="outline" className="rounded-none border-[#111] hover:bg-[#111] hover:text-white transition-colors" data-testid="import-leads-button">
              <FileArrowUp weight="bold" size={16} className="mr-2" /> Import
            </Button>
          )}
          {!isWriter && (
            <Button onClick={openNew} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white transition-colors" data-testid="add-lead-button">
              <Plus weight="bold" size={16} className="mr-2" /> New Lead
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, student, subject, agent…"
            className="rounded-none border-[#111] pl-9" data-testid="lead-search-input" />
        </div>
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="rounded-none border-[#111] w-[160px]" data-testid="filter-country"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Countries</SelectItem>
            {meta.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none border-[#111] w-[150px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Statuses</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="rounded-none border-[#111] w-[150px]" data-testid="filter-month"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Months</SelectItem>
            {monthOptions.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="rounded-none border-[#111] w-[150px]" data-testid="filter-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {isAdmin && (
          <Select value={writerFilter} onValueChange={setWriterFilter}>
            <SelectTrigger className="rounded-none border-[#111] w-[150px]" data-testid="filter-writer"><SelectValue placeholder="Writer" /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All Writers</SelectItem>
              {writers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <button
          onClick={() => setOverdueOnly((v) => !v)}
          data-testid="filter-overdue"
          className={`rounded-none border px-4 flex items-center gap-2 text-sm font-medium transition-colors ${overdueOnly ? "bg-[#e11d48] text-white border-[#e11d48]" : "border-[#111] text-[#111] hover:bg-[#111] hover:text-white"}`}
        >
          <Warning size={15} weight="bold" /> Overdue only
        </button>
      </div>

      {(isAgent || isWriter) && (
        <div className="mb-5" data-testid="month-totals">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#555] mb-2">Totals · {totalsLabel} · {filtered.length} lead(s)</p>
          <div className={`grid gap-3 ${isWriter ? "grid-cols-1 sm:max-w-xs" : "grid-cols-2 lg:grid-cols-5"}`}>
            <div className="bg-white border border-[#111] p-4" data-testid="total-words">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Total Words</p>
              <p className="font-mono-data text-2xl font-bold mt-1">{totals.words.toLocaleString("en-IN")}</p>
            </div>
            {isAgent && (
              <>
                <div className="bg-white border border-[#111] p-4" data-testid="total-received">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Received</p>
                  <p className="font-mono-data text-2xl font-bold mt-1 text-[#00C853]">{inr(totals.received)}</p>
                </div>
                <div className="bg-white border border-[#111] p-4" data-testid="total-pending">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Pending</p>
                  <p className="font-mono-data text-2xl font-bold mt-1 text-[#FFAB00]">{inr(totals.pending)}</p>
                </div>
                <div className="bg-white border border-[#111] p-4" data-testid="total-net">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Net Payment</p>
                  <p className="font-mono-data text-2xl font-bold mt-1 text-[#0047FF]">{inr(totals.net)}</p>
                </div>
                <div className="bg-white border border-[#111] p-4" data-testid="total-avg-per-word">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Avg / Word</p>
                  <p className="font-mono-data text-2xl font-bold mt-1 text-[#0047FF]">{fmtPerWord(avgPerWord)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {isAdmin && agentFilter !== "all" && (
        <div className="mb-5" data-testid="admin-agent-totals">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#555] mb-2">{agentName || "Agent"} · {totalsLabel} · {filtered.length} lead(s)</p>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-6">
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-words">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Total Words</p>
              <p className="font-mono-data text-2xl font-bold mt-1">{totals.words.toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-booking">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Booking</p>
              <p className="font-mono-data text-2xl font-bold mt-1">{inr(totals.booking)}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-pending">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Pending</p>
              <p className="font-mono-data text-2xl font-bold mt-1 text-[#FFAB00]">{inr(totals.pending)}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-net">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Net Payment</p>
              <p className="font-mono-data text-2xl font-bold mt-1 text-[#0047FF]">{inr(totals.net)}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-writer">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Writer Pay</p>
              <p className="font-mono-data text-2xl font-bold mt-1 text-[#8b5cf6]">{inr(totals.writer)}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-total-avg-per-word">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Avg / Word</p>
              <p className="font-mono-data text-2xl font-bold mt-1 text-[#0047FF]">{fmtPerWord(avgPerWord)}</p>
            </div>
          </div>
        </div>
      )}

      {isAdmin && writerFilter !== "all" && (
        <div className="mb-5" data-testid="admin-writer-totals">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#555] mb-2">{writers.find((w) => w.id === writerFilter)?.name || "Writer"} · {totalsLabel} · {filtered.length} lead(s)</p>
          <div className="grid gap-3 grid-cols-2 lg:max-w-md">
            <div className="bg-white border border-[#111] p-4" data-testid="admin-writer-total-words">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Total Words</p>
              <p className="font-mono-data text-2xl font-bold mt-1">{totals.words.toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-white border border-[#111] p-4" data-testid="admin-writer-total-pay">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#555]">Writer Pay</p>
              <p className="font-mono-data text-2xl font-bold mt-1 text-[#8b5cf6]">{inr(totals.writer)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#111] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#111] text-left font-mono text-[10px] uppercase tracking-wider text-[#555]">
              <SortHead label="Created" k="created_at" />
              <SortHead label="Code" k="assignment_code" />
              <th className="px-4 py-3 text-center">Actions</th>
              <SortHead label="Student" k="student_name" />
              {!isAdmin && <SortHead label="Country" k="country" />}
              {!isAdmin && <SortHead label="Subject" k="subject" />}
              <SortHead label="Words" k="word_count" align="right" />
              {!isWriter && <SortHead label="Booking" k="booking_amount" align="right" />}
              {!isWriter && <SortHead label="Received" k="amount_received" align="right" />}
              {!isWriter && <th className="px-4 py-3 text-right">Pending</th>}
              {!isWriter && <th className="px-4 py-3 text-right">Net Pay</th>}
              {!isWriter && <th className="px-4 py-3">Agent</th>}
              {isAdmin && <SortHead label="Writer" k="writer_name" />}
              {isAdmin && <th className="px-4 py-3 text-right">Writer Pay</th>}
              <SortHead label="Status" k="status" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#eee]">
            {filtered.length === 0 && (
              <tr><td colSpan={6 + (isAdmin ? 0 : 2) + (isWriter ? 0 : 5) + (isAdmin ? 2 : 0)} className="px-4 py-10 text-center text-[#999] font-mono text-sm">No leads match.</td></tr>
            )}
            {filtered.map((l) => {
              const pending = (l.booking_amount || 0) - (l.amount_received || 0);
              const locked = !isAdmin && l.status === "Full Payment Received";
              return (
                <tr key={l.id} className="hover:bg-[#fafafa] transition-colors" data-testid={`lead-row-${l.assignment_code}`}>
                  <td className="px-4 py-3 font-mono-data text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-4 py-3 font-mono-data font-semibold text-[#0047FF]">{l.assignment_code}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2.5">
                      {locked && <LockSimple size={16} weight="fill" className="text-[#0a7d2c]" data-testid={`locked-${l.assignment_code}`} />}
                      {!isWriter && !locked && (
                        <button onClick={() => openPayments(l)} className="text-[#0047FF] hover:opacity-70 transition-opacity" title="Payments & receipts" data-testid={`payments-${l.assignment_code}`}>
                          <Money size={17} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => waShare(l)} className="text-[#25D366] hover:opacity-70 transition-opacity" title="Share on WhatsApp" data-testid={`whatsapp-${l.assignment_code}`}>
                        <WhatsappLogo size={17} weight="fill" />
                      </button>
                      {!isWriter && l.student_email && (
                        <button onClick={() => sendReminder(l)} className="text-[#0047FF] hover:opacity-70 transition-opacity" title="Email payment reminder to student" data-testid={`remind-${l.assignment_code}`}>
                          <EnvelopeSimple size={17} weight="bold" />
                        </button>
                      )}
                      {!locked && (
                        <button onClick={() => openEdit(l)} className="text-[#555] hover:text-[#0047FF] transition-colors" title="Edit" data-testid={`edit-lead-${l.assignment_code}`}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => remove(l.id)} className="text-[#e11d48] hover:opacity-70 transition-opacity" title="Delete" data-testid={`delete-lead-${l.assignment_code}`}>
                          <Trash size={16} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="font-medium">{l.student_name}</p>
                    {!isWriter && (l.student_phone || (!isAdmin && l.student_email)) && (
                      <p className="font-mono-data text-[11px] text-[#555]">{[l.student_phone, !isAdmin ? l.student_email : null].filter(Boolean).join(" · ")}</p>
                    )}
                  </td>
                  {!isAdmin && <td className="px-4 py-3 whitespace-nowrap">{l.country}</td>}
                  {!isAdmin && <td className="px-4 py-3 max-w-[150px] truncate" title={l.subject}>{l.subject}</td>}
                  <td className="px-4 py-3 text-right font-mono-data">{Number(l.word_count).toLocaleString()}</td>
                  {!isWriter && <td className="px-4 py-3 text-right font-mono-data">{inr(l.booking_amount)}</td>}
                  {!isWriter && <td className="px-4 py-3 text-right font-mono-data text-[#00C853]">{inr(l.amount_received)}</td>}
                  {!isWriter && <td className={`px-4 py-3 text-right font-mono-data ${pending > 0 ? "text-[#FFAB00]" : "text-[#999]"}`}>{inr(pending)}</td>}
                  {!isWriter && <td className="px-4 py-3 text-right font-mono-data text-[#0047FF]">{l.net_payment != null ? inr(l.net_payment) : "—"}</td>}
                  {!isWriter && <td className="px-4 py-3 whitespace-nowrap text-xs">{l.agent_name || "—"}</td>}
                  {isAdmin && <td className="px-4 py-3 whitespace-nowrap text-xs">{l.writer_name || "—"}</td>}
                  {isAdmin && <td className="px-4 py-3 text-right font-mono-data text-[#8b5cf6]">{l.writer_payment_amount != null ? inr(l.writer_payment_amount) : "—"}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge className={`rounded-none ${statusColor[l.status] || "bg-[#555] text-white"}`}>{l.status}</Badge>
                      {locked && (
                        <Badge className="rounded-none bg-[#0a7d2c] text-white gap-1" data-testid={`locked-badge-${l.assignment_code}`}>
                          <LockSimple size={11} weight="fill" />LOCKED
                        </Badge>
                      )}
                      {l.is_overdue && (
                        <Badge className="rounded-none bg-[#e11d48] text-white gap-1" data-testid={`overdue-badge-${l.assignment_code}`}>
                          <Warning size={11} weight="bold" />OVERDUE
                        </Badge>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#111] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">{editing ? `Edit ${editing.assignment_code}` : "New Lead"}</DialogTitle>
            {!editing && <p className="text-xs text-[#555] font-mono">Assignment code auto-generates (MHA11000+) on save.</p>}
          </DialogHeader>

          {isWriter ? (
            <div>
              <Label className="text-xs font-mono uppercase">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{meta.statuses.filter((s) => isAdmin || s !== "Full Payment Received").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isAdmin && editing && (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-mono uppercase">Assignment Code (admin editable)</Label>
                  <Input className="rounded-none border-[#111] mt-1 font-mono-data" value={form.assignment_code || ""} onChange={(e) => setForm({ ...form, assignment_code: e.target.value })} data-testid="lead-code-input" />
                </div>
              )}
              {isAdmin && editing && (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-mono uppercase">Created Date (admin editable)</Label>
                  <Input type="date" className="rounded-none border-[#111] mt-1 font-mono-data" value={(form.created_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, created_at: e.target.value })} data-testid="lead-created-input" />
                </div>
              )}
              <div>
                <Label className="text-xs font-mono uppercase">Student Name</Label>
                <Input className="rounded-none border-[#111] mt-1" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="lead-student-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Student WhatsApp (with country code)</Label>
                <Input className="rounded-none border-[#111] mt-1 font-mono-data" placeholder="447700900000" value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} data-testid="lead-phone-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Student Email (for reminders)</Label>
                <Input type="email" className="rounded-none border-[#111] mt-1" placeholder="student@email.com" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} data-testid="lead-email-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="lead-country-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="rounded-none">{meta.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Subject / Module</Label>
                <Input className="rounded-none border-[#111] mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="lead-subject-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Word Count</Label>
                <Input type="number" className="rounded-none border-[#111] mt-1 font-mono-data" value={form.word_count} onChange={(e) => setForm({ ...form, word_count: e.target.value })} data-testid="lead-words-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Expected Completion</Label>
                <Input type="date" className="rounded-none border-[#111] mt-1 font-mono-data" value={form.expected_completion_date} onChange={(e) => setForm({ ...form, expected_completion_date: e.target.value })} data-testid="lead-date-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Booking Amount (INR)</Label>
                <Input type="number" className="rounded-none border-[#111] mt-1 font-mono-data" value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} data-testid="lead-booking-input" />
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-xs font-mono uppercase">Net Payment after Deductions (INR)</Label>
                  <Input type="number" className="rounded-none border-[#111] mt-1 font-mono-data" value={form.net_payment} onChange={(e) => setForm({ ...form, net_payment: e.target.value })} data-testid="lead-netpay-input" />
                </div>
              )}
              {isAdmin && (
                <div>
                  <Label className="text-xs font-mono uppercase">Agent</Label>
                  <Select value={form.agent_id || ""} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
                    <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="lead-agent-select"><SelectValue placeholder="Assign agent" /></SelectTrigger>
                    <SelectContent className="rounded-none">{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && (
                <div>
                  <Label className="text-xs font-mono uppercase">Writer</Label>
                  <Select value={form.writer_id || ""} onValueChange={(v) => setForm({ ...form, writer_id: v })}>
                    <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="lead-writer-select"><SelectValue placeholder="Assign writer" /></SelectTrigger>
                    <SelectContent className="rounded-none">{writers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-mono uppercase">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{meta.statuses.filter((s) => isAdmin || s !== "Full Payment Received").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-mono uppercase">Notes</Label>
                <Textarea className="rounded-none border-[#111] mt-1" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-notes-input" />
              </div>
              <div className="sm:col-span-2 flex items-center gap-2 text-xs text-[#555] font-mono border-t border-[#eee] pt-3">
                <Receipt size={14} /> Record part-payments &amp; receipts from the Payments icon after saving.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={save} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white transition-colors" data-testid="save-lead-button">
              {editing ? "Save changes" : "Create lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payments dialog */}
      <Dialog open={!!payLead} onOpenChange={(o) => !o && setPayLead(null)}>
        <DialogContent className="rounded-none border-[#111] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">Payments · {payLead?.assignment_code}</DialogTitle>
            {payLead && (
              <p className="text-xs text-[#555] font-mono">
                Booking {inr(payLead.booking_amount)} · Received {inr(payLead.amount_received)} · Pending {inr((payLead.booking_amount || 0) - (payLead.amount_received || 0))}
              </p>
            )}
          </DialogHeader>

          {payLead && (
            <>
              <div className="space-y-2 max-h-[240px] overflow-y-auto">
                {(payLead.payments || []).length === 0 && <p className="text-sm text-[#999] font-mono py-3">No payments recorded yet.</p>}
                {(payLead.payments || []).map((p) => (
                  <div key={p.id} className="flex items-center justify-between border border-[#eee] px-3 py-2" data-testid={`payment-${p.id}`}>
                    <div className="flex items-center gap-3">
                      <Badge className={`rounded-none ${p.party === "student" ? "bg-[#00C853] text-white" : "bg-[#0047FF] text-white"}`}>{p.party === "student" ? "IN" : "OUT"}</Badge>
                      <div>
                        <p className="font-mono-data font-semibold">{inr(p.amount)}</p>
                        <p className="text-[11px] text-[#555]">{p.date || "—"} {p.note ? `· ${p.note}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {p.receipt && (
                        <button onClick={() => viewReceipt(p.receipt)} className="text-[#0047FF] hover:opacity-70 flex items-center gap-1 transition-opacity" data-testid={`view-receipt-${p.id}`}>
                          <FilePdf size={16} weight="bold" /><span className="text-xs underline">Receipt</span>
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => deletePayment(p.id)} className="text-[#e11d48] hover:opacity-70 transition-opacity" data-testid={`delete-payment-${p.id}`}>
                          <Trash size={15} weight="bold" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#111] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase">Type</Label>
                  <Select value={payForm.party} onValueChange={(v) => setPayForm({ ...payForm, party: v })}>
                    <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="payment-party-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="student">Received from Student</SelectItem>
                      {isAdmin && <SelectItem value="writer">Paid to Writer</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Amount (INR)</Label>
                  <Input type="number" className="rounded-none border-[#111] mt-1 font-mono-data" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Date</Label>
                  <Input type="date" className="rounded-none border-[#111] mt-1 font-mono-data" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="payment-date-input" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Note</Label>
                  <Input className="rounded-none border-[#111] mt-1" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="payment-note-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-mono uppercase">Receipt (PDF / JPEG)</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="rounded-none border-[#111] mt-1" onChange={(e) => setPayForm({ ...payForm, file: e.target.files[0] })} data-testid="payment-file-input" />
                </div>
              </div>

              <DialogFooter>
                <Button onClick={addPayment} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white transition-colors" data-testid="add-payment-button">
                  <Plus weight="bold" size={16} className="mr-2" /> Add Payment
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none border-[#111] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-2xl">Bulk Import Leads</DialogTitle>
            <p className="text-xs text-[#555] font-mono">Upload an Excel (.xlsx) or CSV file. Agent/Writer are matched by name.</p>
          </DialogHeader>
          <div className="space-y-4">
            <Button onClick={downloadTemplate} variant="outline" className="rounded-none border-[#111] w-full hover:bg-[#111] hover:text-white transition-colors" data-testid="download-template-button">
              <DownloadSimple weight="bold" size={16} className="mr-2" /> Download Excel template
            </Button>
            <div>
              <Label className="text-xs font-mono uppercase">Choose file (.xlsx / .csv)</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" className="rounded-none border-[#111] mt-1" onChange={(e) => setImportFile(e.target.files[0])} data-testid="import-file-input" />
            </div>
            {importResult && (
              <div className="border border-[#111] p-3 text-sm font-mono-data" data-testid="import-result">
                <p className="text-[#00C853] font-semibold">{importResult.created} imported</p>
                <p className="text-[#555]">of {importResult.total_rows} rows</p>
                {importResult.errors?.length > 0 && (
                  <ul className="text-[#e11d48] mt-2 list-disc pl-4">{importResult.errors.slice(0, 5).map((er, i) => <li key={i}>{er}</li>)}</ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={runImport} disabled={importing} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white transition-colors" data-testid="run-import-button">
              <FileArrowUp weight="bold" size={16} className="mr-2" /> {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
